import { useMemo, useState } from 'react'
import { Box, Divider, InputBase, ListItemIcon, ListItemText, Menu, MenuItem, ThemeProvider, Typography } from '@mui/material'
import { KeyboardArrowDown, People, Person, Sync } from '@mui/icons-material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useNavbar } from '@/contexts/NavbarContext'
import { useAthlete } from '@/contexts/AthleteContext'
import { apiClient } from '@/services/api/client'
import { getDjangoOrigin } from '@/lib/djangoOrigin'
import { beoflowColors, borderRadius, createChromeTheme, spacing, typography } from '@/theme/muiTheme'

const LENS_CONTROL_HEIGHT = 34

const LENS_DASHBOARDS = [
  { path: '/lens/sleep', label: 'Sleep' },
  { path: '/lens/recovery', label: 'Recovery' },
  { path: '/lens/exercise', label: 'Exercise' },
] as const

function athleteDisplay(athlete: { name?: string | null; email?: string | null }) {
  return athlete.name?.trim() || athlete.email?.trim() || 'Unknown athlete'
}

export default function LensWebViewHeader() {
  const { title, subtitle, showAthleteSelector } = useNavbar()
  const athleteCtx = useAthlete()
  const navigate = useNavigate()
  const location = useLocation()
  const [athleteAnchorEl, setAthleteAnchorEl] = useState<null | HTMLElement>(null)
  const [athleteSearch, setAthleteSearch] = useState('')
  const [dashboardAnchorEl, setDashboardAnchorEl] = useState<null | HTMLElement>(null)

  const currentDashboard =
    LENS_DASHBOARDS.find((d) => location.pathname.startsWith(d.path)) ?? LENS_DASHBOARDS[0]
  const showDashboardSwitcher = LENS_DASHBOARDS.some((d) => location.pathname.startsWith(d.path))

  const athleteMenuOpen = Boolean(athleteAnchorEl)
  // createChromeTheme('dark'), not createBeoflowTheme('dark'): the Lens page itself is dark
  // (App.tsx wraps it in lensDarkTheme), so dropdowns here are correctly dark. Saying so
  // explicitly keeps it from drifting if the page ever stops being dark.
  const darkHeaderTheme = useMemo(() => createChromeTheme('dark'), [])
  // Inside the iOS webview the native Lens header owns the athlete selector, so
  // hide the in-page one. Plain browser /lens keeps it.
  const inIosWebView = /beoflow_mobile_app/i.test(navigator.userAgent)
  const showAthleteChip = showAthleteSelector && athleteCtx.isCoach && !inIosWebView
  const selectedAthlete = useMemo(
    () => athleteCtx.athletes.find((athlete) => athlete.profile_id === athleteCtx.selectedProfileId) ?? null,
    [athleteCtx.athletes, athleteCtx.selectedProfileId],
  )

  const groupedAthletes = useMemo(() => {
    const query = athleteSearch.trim().toLowerCase()
    const groups = new Map<string, typeof athleteCtx.athletes>()
    for (const athlete of athleteCtx.athletes) {
      if (athlete.profile_id === athleteCtx.selectedProfileId) continue
      const searchable = `${athleteDisplay(athlete)} ${athlete.email ?? ''}`.toLowerCase()
      if (query && !searchable.includes(query)) continue
      const groupName = athlete.group_name || 'Ungrouped'
      groups.set(groupName, [...(groups.get(groupName) ?? []), athlete])
    }
    return [...groups.entries()]
  }, [athleteCtx.athletes, athleteCtx.selectedProfileId, athleteSearch])

  const openIntegrationsSync = async () => {
    try {
      await apiClient.post('/auth_account/session-bridge/')
    } catch {
      // Bridge failed -- open anyway, user will see login page.
    }

    const url = new URL(`${getDjangoOrigin()}/auth_account/processing-preferences/`, window.location.origin)
    url.searchParams.set('source', 'webapp')
    if (selectedAthlete?.user_id) {
      url.searchParams.set('target_user_id', String(selectedAthlete.user_id))
    } else if (athleteCtx.selectedProfileId) {
      url.searchParams.set('target_profile_id', String(athleteCtx.selectedProfileId))
    }
    window.open(url.toString(), '_blank')
  }

  if (!title && !subtitle && !showAthleteChip && !showDashboardSwitcher) return null

  return (
    <>
      <ThemeProvider theme={darkHeaderTheme}>
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 20,
            backgroundColor: beoflowColors.beoBlacker,
            color: beoflowColors.beoWhite,
            px: spacing.md,
            py: spacing.sm,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              minWidth: 0,
              overflowX: 'auto',
              pb: '2px',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {subtitle && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                  minWidth: 'max-content',
                  flex: 1,
                  '& .MuiIconButton-root': {
                    display: 'none',
                  },
                  '& .MuiFormControl-root': {
                    minWidth: '0 !important',
                  },
                  '& .MuiInputBase-root': {
                    height: LENS_CONTROL_HEIGHT,
                    minHeight: LENS_CONTROL_HEIGHT,
                    // Fully rounded pill (matches the range "Xd" chip) regardless of the
                    // dashboards' overridden control height.
                    borderRadius: borderRadius.round,
                    fontSize: `${typography.fontSizeXs} !important`,
                  },
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: borderRadius.round,
                  },
                  '& .MuiInputBase-root:not(:has(input[type="date"]))': {
                    width: 58,
                  },
                  '& .MuiInputBase-root:has(input[type="date"])': {
                    width: 138,
                  },
                  '& .MuiSelect-icon': {
                    display: 'none',
                  },
                  '& .MuiSelect-select.MuiSelect-outlined': {
                    paddingRight: '8px !important',
                  },
                  '& .MuiSelect-select': {
                    height: `${LENS_CONTROL_HEIGHT}px !important`,
                    minHeight: `${LENS_CONTROL_HEIGHT}px !important`,
                    py: '0 !important',
                    pl: '8px !important',
                    pr: '8px !important',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxSizing: 'border-box',
                    lineHeight: `${LENS_CONTROL_HEIGHT}px`,
                    textAlign: 'center',
                  },
                  '& input[type="date"]': {
                    height: `${LENS_CONTROL_HEIGHT}px !important`,
                    py: '0 !important',
                    px: '6px !important',
                    lineHeight: `${LENS_CONTROL_HEIGHT}px`,
                    boxSizing: 'border-box',
                    textAlign: 'center',
                  },
                  '& input[type="date"]::-webkit-date-and-time-value': {
                    textAlign: 'center',
                    minHeight: `${LENS_CONTROL_HEIGHT}px`,
                  },
                }}
              >
                {subtitle}
              </Box>
            )}

            {showDashboardSwitcher && (
              <Box
                onClick={(event) => setDashboardAnchorEl(event.currentTarget)}
                role="button"
                aria-label={`Switch dashboard (currently ${currentDashboard.label})`}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: spacing.sm,
                  height: LENS_CONTROL_HEIGHT,
                  py: 0,
                  borderRadius: borderRadius.round,
                  backgroundColor: beoflowColors.beoWhite,
                  cursor: 'pointer',
                  flexShrink: 0,
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: beoflowColors.graphGreyLight },
                }}
              >
                <Typography
                  noWrap
                  sx={{
                    fontFamily: typography.fontFamilyMonospace,
                    fontSize: typography.fontSizeTiny,
                    fontWeight: typography.fontWeightSemibold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterButton,
                    color: beoflowColors.beoBlack,
                  }}
                >
                  {currentDashboard.label}
                </Typography>
                <KeyboardArrowDown sx={{ fontSize: 16, color: beoflowColors.beoBlack }} />
              </Box>
            )}

            {showAthleteChip && (
              <Box
                onClick={(event) => setAthleteAnchorEl(event.currentTarget)}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  px: spacing.sm,
                  height: LENS_CONTROL_HEIGHT,
                  py: 0,
                  borderRadius: borderRadius.round,
                  backgroundColor: beoflowColors.meanBlue,
                  cursor: 'pointer',
                  minWidth: 0,
                  maxWidth: 210,
                  flexShrink: 0,
                  transition: 'background-color 0.2s ease',
                  '&:hover': { backgroundColor: beoflowColors.blueDark },
                }}
              >
                {athleteCtx.selectedProfileId ? (
                  <People sx={{ fontSize: 14, color: beoflowColors.beoWhite }} />
                ) : (
                  <Person sx={{ fontSize: 14, color: beoflowColors.beoWhite }} />
                )}
                <Typography
                  noWrap
                  sx={{
                    fontFamily: typography.fontFamilyMonospace,
                    fontSize: typography.fontSizeTiny,
                    fontWeight: typography.fontWeightSemibold,
                    textTransform: 'uppercase',
                    letterSpacing: typography.letterButton,
                    color: beoflowColors.beoWhite,
                    maxWidth: 132,
                  }}
                >
                  {selectedAthlete ? athleteDisplay(selectedAthlete) : athleteCtx.selectedProfileName ?? 'Myself'}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </ThemeProvider>

      <Menu
        anchorEl={dashboardAnchorEl}
        open={Boolean(dashboardAnchorEl)}
        onClose={() => setDashboardAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{ paper: { sx: { minWidth: 180 } } }}
      >
        {LENS_DASHBOARDS.map((dashboard) => (
          <MenuItem
            key={dashboard.path}
            selected={dashboard.path === currentDashboard.path}
            onClick={() => {
              navigate(dashboard.path)
              setDashboardAnchorEl(null)
            }}
          >
            <ListItemText>{dashboard.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>

      <Menu
        anchorEl={athleteAnchorEl}
        open={athleteMenuOpen}
        onClose={() => setAthleteAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              minWidth: 240,
              maxHeight: 380,
            },
          },
        }}
      >
        <MenuItem selected>
          <ListItemIcon>{selectedAthlete ? <People fontSize="small" /> : <Person fontSize="small" />}</ListItemIcon>
          <ListItemText>{selectedAthlete ? athleteDisplay(selectedAthlete) : 'Myself'}</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => {
            void openIntegrationsSync()
            setAthleteAnchorEl(null)
          }}
        >
          <ListItemIcon><Sync fontSize="small" /></ListItemIcon>
          <ListItemText>Sync data</ListItemText>
        </MenuItem>
        <Divider />
        <Box sx={{ px: spacing.md, py: spacing.sm }}>
          <InputBase
            autoFocus
            placeholder="Search athletes"
            value={athleteSearch}
            onChange={(event) => setAthleteSearch(event.target.value)}
            onKeyDown={(event) => event.stopPropagation()}
            sx={{
              width: '100%',
              px: spacing.sm,
              py: '4px',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: borderRadius.medium,
              fontFamily: typography.fontFamilyMonospace,
              fontSize: typography.fontSizeSm,
            }}
          />
        </Box>
        {athleteCtx.selectedProfileId !== null && (
          <MenuItem
            onClick={() => {
              athleteCtx.setSelectedProfile(null, null)
              setAthleteSearch('')
              setAthleteAnchorEl(null)
            }}
          >
            <ListItemIcon><Person fontSize="small" /></ListItemIcon>
            <ListItemText>Myself</ListItemText>
          </MenuItem>
        )}
        {groupedAthletes.map(([groupName, athletes]) => (
          <Box key={groupName}>
            <Typography variant="monoSmall" sx={{ px: spacing.md, pt: spacing.sm, pb: spacing.xs, color: 'text.secondary' }}>
              {groupName}
            </Typography>
            {athletes.map((athlete) => (
              <MenuItem
                key={athlete.profile_id}
                onClick={() => {
                  athleteCtx.setSelectedProfile(athlete.profile_id, athleteDisplay(athlete))
                  setAthleteSearch('')
                  setAthleteAnchorEl(null)
                }}
              >
                <ListItemIcon><People fontSize="small" /></ListItemIcon>
                <ListItemText>{athleteDisplay(athlete)}</ListItemText>
              </MenuItem>
            ))}
          </Box>
        ))}
        {!athleteCtx.isLoading && athleteCtx.athletes.length === 0 && (
          <MenuItem disabled><ListItemText>No athletes available</ListItemText></MenuItem>
        )}
        {!athleteCtx.isLoading && athleteCtx.athletes.length > 0 && groupedAthletes.length === 0 && athleteCtx.selectedProfileId === null && (
          <MenuItem disabled><ListItemText>No matching athletes</ListItemText></MenuItem>
        )}
      </Menu>
    </>
  )
}
